import type { InventoryBalance, ZoneType } from "../api/types";
import { unknownQtyNotCountedNote } from "./unknownCost";

/**
 * G-132 — one row per PRODUCT. The balance read returns one row per item per
 * zone per shop, so the same pale-pink hydrangea used to appear four times.
 * This module folds those rows into one product with its placements, and owns
 * the zone chips and sort over the folded rows — the component renders and
 * this decides (same split as the zone filter it replaces, G-92).
 */

export interface InventoryProductRow {
  item: InventoryBalance["item"];
  /** Sum over every placement with stock. */
  totalQty: number;
  /** Rows with stock, sorted shop → zone. */
  placements: InventoryBalance[];
  /** The most recent stock-in over all placements. */
  lastStockInAt: string | null;
  /**
   * Quantity-weighted average of the placements' purchase cost. Rows with no
   * cost, and units of unknown cost (WO-B part 4), are left out of the
   * average; null when none has one — the honest unknown, never €0.
   */
  unitCostEur: number | null;
  /**
   * WO-B part 6: units on the shelf that `unitCostEur` does not speak for —
   * of unknown cost, or with no lot behind them (`unknownCostQtyOf`). 0 where
   * the price column does not apply (flowers, perishables).
   */
  unknownCostQty: number;
}

/** A product as seen through a zone filter: `zoneQty` is what that zone holds. */
export type InventoryProductView = InventoryProductRow & { zoneQty: number };

function qtyOf(row: InventoryBalance): number {
  const n = parseFloat(row.qtyOnHand);
  return Number.isFinite(n) ? n : 0;
}

function decimalOf(value: string | null | undefined): number {
  const n = value != null ? parseFloat(value) : NaN;
  return Number.isFinite(n) ? n : 0;
}

/**
 * WO-B part 4 (OD-198): the units on this row that are CERTAINLY of known cost
 * — what a value may multiply `avgPurchaseUnitCostEur` by. The average speaks
 * for the lots' known units (`knownCostQty`); the row's quantity comes from
 * the balance, and the two can disagree (KI-1). So: never more than the lots'
 * known units (units with no lot have no cost), and never a unit that could
 * be one of the lots' unknown ones (the row less every unknown unit). In the
 * normal case — balance and lots agree — that is exactly `knownCostQty`.
 * Multiplying by the whole quantity would price unknown units at the average
 * of the known ones: a confident, made-up figure. Absent (a florist's kiosk
 * row): 0 — nothing to value.
 */
export function knownCostQtyOf(row: InventoryBalance): number {
  const known = decimalOf(row.knownCostQty);
  const unknown = decimalOf(row.unknownCostQty);
  return Math.max(0, Math.min(known, qtyOf(row) - unknown));
}

/**
 * WO-B part 6 (OD-198): the units on this row no known cost speaks for — the
 * rest of `knownCostQtyOf`: lots of unknown cost and units with no lot. Null
 * where the price column does not apply (flowers, perishables) or is hidden
 * (a florist's kiosk row) — nothing to say there.
 */
export function unknownCostQtyOf(row: InventoryBalance): number | null {
  if (row.knownCostQty == null) return null;
  return Math.max(0, qtyOf(row) - knownCostQtyOf(row));
}

/**
 * WO-B part 6: what a product's price reads. `unitCostEur` — the average of
 * the known costs; `costUnknown` — no average, but stock of unknown cost on
 * the shelf ("cost unknown"); `uncountedNote` — beside an average, the units
 * it leaves out, in the item's own unit ("3 STEM of unknown cost not counted").
 * Product-wide, like the average it sits under — also when a zone is chosen.
 */
export function productCostView(
  product: Pick<InventoryProductRow, "unitCostEur" | "unknownCostQty"> & {
    item: Pick<InventoryProductRow["item"], "defaultUom">;
  },
): {
  unitCostEur: number | null;
  costUnknown: boolean;
  uncountedNote: string | null;
} {
  const known = product.unitCostEur != null;
  return {
    unitCostEur: product.unitCostEur,
    costUnknown: !known && product.unknownCostQty > 0,
    uncountedNote: known
      ? unknownQtyNotCountedNote(product.unknownCostQty, product.item.defaultUom)
      : null,
  };
}

function byName(a: { item: { name: string } }, b: { item: { name: string } }): number {
  return a.item.name.localeCompare(b.item.name, undefined, { sensitivity: "base" });
}

/** Folds balance rows into one row per item. Only rows holding stock count. */
export function groupInventoryByItem(rows: InventoryBalance[]): InventoryProductRow[] {
  const byItem = new Map<string, InventoryBalance[]>();
  for (const row of rows) {
    if (qtyOf(row) <= 0) continue;
    const list = byItem.get(row.item.id);
    if (list) list.push(row);
    else byItem.set(row.item.id, [row]);
  }

  const products: InventoryProductRow[] = [];
  for (const placementsRaw of byItem.values()) {
    const placements = [...placementsRaw].sort(
      (a, b) =>
        a.location.name.localeCompare(b.location.name, undefined, { sensitivity: "base" }) ||
        a.zone.name.localeCompare(b.zone.name, undefined, { sensitivity: "base" }),
    );

    let totalQty = 0;
    let costQty = 0;
    let costSum = 0;
    let unknownCostQty = 0;
    let lastStockInAt: string | null = null;
    let lastMs = -Infinity;
    for (const p of placements) {
      const qty = qtyOf(p);
      totalQty += qty;
      const cost = p.avgPurchaseUnitCostEur != null ? parseFloat(p.avgPurchaseUnitCostEur) : NaN;
      if (Number.isFinite(cost)) {
        // An AVERAGE weighs each placement by the lots its average speaks for
        // (WO-B part 4) — exact whatever the balance says; a VALUE uses
        // `knownCostQtyOf` instead.
        const known = decimalOf(p.knownCostQty);
        costQty += known;
        costSum += known * cost;
      }
      unknownCostQty += unknownCostQtyOf(p) ?? 0;
      if (p.lastStockInAt) {
        const ms = Date.parse(p.lastStockInAt);
        if (Number.isFinite(ms) && ms > lastMs) {
          lastMs = ms;
          lastStockInAt = p.lastStockInAt;
        }
      }
    }
    products.push({
      item: placements[0]!.item,
      totalQty,
      placements,
      lastStockInAt,
      unitCostEur: costQty > 0 ? costSum / costQty : null,
      unknownCostQty,
    });
  }
  return products.sort(byName);
}

/** A quantity for display: sums of decimals are rounded so 0.1 + 0.2 reads 0.3. */
export function formatQty(n: number): string {
  return String(Math.round(n * 1000) / 1000);
}

// ---- Tags ------------------------------------------------------------------

export interface PlacementTag {
  key: string;
  label: string;
  qty: number;
}

/**
 * A zone's short name for a tag. The three shelf areas read as one word; a
 * storage box (G-84) drops its "BOX-" prefix so "BOX-000001" reads "Box
 * 000001"; any other zone keeps its own name.
 */
export function zoneTagLabel(zone: { name: string; zoneType: ZoneType }): string {
  if (zone.zoneType === "FRONT_SHOP") return "Front";
  if (zone.zoneType === "FRIDGE") return "Fridge";
  if (zone.zoneType === "DRY_STORAGE") return "Storage";
  const box = /^BOX-(.+)$/i.exec(zone.name);
  if (zone.zoneType === "BOX" || box) return `Box ${box ? box[1] : zone.name}`;
  return zone.name;
}

/**
 * The tag strip under a product's name: one tag per SHOP when the rows span
 * several (the All-shops view), one per zone otherwise. Same-label zones in
 * one shop merge, so two fridges read as one "Fridge 12".
 */
export function placementTags(product: InventoryProductRow, byShop: boolean): PlacementTag[] {
  const tags = new Map<string, PlacementTag>();
  for (const p of product.placements) {
    const key = byShop ? p.location.id : zoneTagLabel(p.zone);
    const label = byShop ? p.location.name : key;
    const found = tags.get(key);
    if (found) found.qty += qtyOf(p);
    else tags.set(key, { key, label, qty: qtyOf(p) });
  }
  return [...tags.values()];
}

// ---- Where chips -----------------------------------------------------------

export const ALL_ZONES = "ALL" as const;

export type InventoryZoneFilter = typeof ALL_ZONES | ZoneType;

export interface InventoryZoneGroup {
  key: InventoryZoneFilter;
  label: string;
  /** Products with stock in this area. */
  count: number;
}

/** "Storage" and "Front store" are the words the rest of the page already uses. */
const ZONE_LABELS: { zoneType: ZoneType; label: string }[] = [
  { zoneType: "FRONT_SHOP", label: "Front store" },
  { zoneType: "FRIDGE", label: "Fridge" },
  { zoneType: "DRY_STORAGE", label: "Storage" },
  { zoneType: "CUSTOM", label: "Other" },
];

/** A zone type with no chip of its own (a storage box is CUSTOM) lives under Other. */
function zoneBucket(zoneType: ZoneType): ZoneType {
  return ZONE_LABELS.some((z) => z.zoneType === zoneType) ? zoneType : "CUSTOM";
}

/**
 * The chips, "All" first then shelf order. An area holding nothing is left
 * out; "All" is always offered because it is where the list starts.
 */
export function productZoneGroups(products: InventoryProductRow[]): InventoryZoneGroup[] {
  const counts = new Map<ZoneType, number>();
  for (const product of products) {
    const buckets = new Set(product.placements.map((p) => zoneBucket(p.zone.zoneType)));
    for (const bucket of buckets) counts.set(bucket, (counts.get(bucket) ?? 0) + 1);
  }
  const groups: InventoryZoneGroup[] = [
    { key: ALL_ZONES, label: "All", count: products.length },
  ];
  for (const { zoneType, label } of ZONE_LABELS) {
    const count = counts.get(zoneType) ?? 0;
    if (count > 0) groups.push({ key: zoneType, label, count });
  }
  return groups;
}

/**
 * Products with a placement in the zone. `zoneQty` is what that zone holds
 * (the whole total under All); `placements` stays complete so the tags still
 * show every place the product is.
 */
export function filterProductsByZone(
  products: InventoryProductRow[],
  filter: InventoryZoneFilter,
): InventoryProductView[] {
  if (filter === ALL_ZONES) return products.map((p) => ({ ...p, zoneQty: p.totalQty }));
  const views: InventoryProductView[] = [];
  for (const product of products) {
    const inZone = product.placements.filter((p) => zoneBucket(p.zone.zoneType) === filter);
    if (inZone.length === 0) continue;
    views.push({ ...product, zoneQty: inZone.reduce((sum, p) => sum + qtyOf(p), 0) });
  }
  return views;
}

/** Keeps the chosen chip honest when the products change underneath it. */
export function resolveZoneFilter(
  current: InventoryZoneFilter,
  groups: InventoryZoneGroup[],
): InventoryZoneFilter {
  return groups.some((g) => g.key === current) ? current : ALL_ZONES;
}

// ---- Sort ------------------------------------------------------------------

export type InventoryProductSortKey = "name" | "qty" | "lastIn";

/**
 * Name, the quantity on show (`zoneQty` when a zone is chosen), or the last
 * stock-in. Ties fall back to name; a product never received sorts last
 * whichever way the date runs.
 */
export function sortInventoryProducts<T extends InventoryProductRow & { zoneQty?: number }>(
  products: T[],
  key: InventoryProductSortKey,
  dir: "asc" | "desc",
): T[] {
  const mult = dir === "asc" ? 1 : -1;
  return [...products].sort((a, b) => {
    if (key === "name") return mult * byName(a, b);
    if (key === "lastIn") {
      const ta = a.lastStockInAt ? Date.parse(a.lastStockInAt) : NaN;
      const tb = b.lastStockInAt ? Date.parse(b.lastStockInAt) : NaN;
      const hasA = Number.isFinite(ta);
      const hasB = Number.isFinite(tb);
      if (!hasA && !hasB) return byName(a, b);
      if (!hasA) return 1;
      if (!hasB) return -1;
      if (ta === tb) return byName(a, b);
      return mult * (ta < tb ? -1 : 1);
    }
    const qa = a.zoneQty ?? a.totalQty;
    const qb = b.zoneQty ?? b.totalQty;
    if (qa === qb) return byName(a, b);
    return mult * (qa < qb ? -1 : 1);
  });
}
